import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { AmyWidget } from "@/components/AmyWidget";
import faviconAsset from "../assets/favicon.webp.asset.json";
import favicon32 from "../assets/favicon-32.png.asset.json";
import favicon192 from "../assets/favicon-192.png.asset.json";
import favicon512 from "../assets/favicon-512.png.asset.json";
import logoAsset from "../assets/logo.webp.asset.json";
import ogImageAsset from "../assets/og-image.jpg.asset.json";

const SITE_URL = "https://vendingmachinefree.com";
const OG_IMAGE = `${SITE_URL}${ogImageAsset.url}`;
const GA4_ID = (import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined) ?? "";
void logoAsset;

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">Something went wrong on our end.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent hover:text-accent-foreground"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "Vending Machine Free.com" },
      { property: "og:site_name", content: "Vending Machine Free.com" },
      { property: "og:type", content: "website" },
      { name: "google-site-verification", content: "xVwYbHwPlAX5_kT4W18BYD-xFDoxtlxSbzpkG4C_-60" },
      { name: "google-site-verification", content: "fDIfUP-Ehv43ZTDqYDSfLSBnFmvoTmRPWHgfOaX4HQ8" },
      { name: "google-site-verification", content: "3Gs316A1OSgelV2k10AXEMBiv6-GYDqUex5rEuayIak" },
      { name: "msvalidate.01", content: "A1F2A434705776B727851A9BFA9CBE4B" },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:alt", content: "Vending Machine Free.com — Free vending machines nationwide USA" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:type", content: "image/jpeg" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@vendingmachines" },
      { name: "twitter:image", content: OG_IMAGE },
      { name: "twitter:image:alt", content: "Vending Machine Free.com — Free vending machines nationwide USA" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", sizes: "32x32", href: favicon32.url },
      { rel: "icon", type: "image/png", sizes: "192x192", href: favicon192.url },
      { rel: "icon", type: "image/png", sizes: "512x512", href: favicon512.url },
      { rel: "icon", type: "image/webp", href: faviconAsset.url },
      { rel: "apple-touch-icon", sizes: "192x192", href: favicon192.url },
      { rel: "sitemap", type: "application/xml", href: "/sitemap-index.xml" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "preload",
        as: "style",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Oswald:wght@500;600;700&display=swap",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Oswald:wght@500;600;700&display=swap",
      },
    ],
    scripts: GA4_ID
      ? [
          {
            src: `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`,
            async: true,
          },
          {
            children: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${GA4_ID}',{anonymize_ip:true});`,
          },
        ]
      : [],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
        <meta name="google-site-verification" content="fDIfUP-Ehv43ZTDqYDSfLSBnFmvoTmRPWHgfOaX4HQ8" />
        <meta name="google-site-verification" content="3Gs316A1OSgelV2k10AXEMBiv6-GYDqUex5rEuayIak" />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
      <div
        role="contentinfo"
        aria-label="Site tagline"
        className="w-full bg-[#222222] text-center py-3 px-4 border-t-4 border-[#F57C00]"
      >
        <p className="text-sm md:text-base font-semibold uppercase tracking-wide text-[#F57C00]">
          Powered by American businesses — get a Free Vending Machine Nationwide.
        </p>
      </div>
      <AmyWidget />
    </QueryClientProvider>
  );
}
